import { useProjectStore, useTrackStore } from '@ve-pro/store'
import type {
  RatioTypeUnion,
  SegmentCollection,
  SegmentMaterial,
  SegmentReplaceableCollection,
  SegmentMuteCollection,
  SegmentGroups,
  SegmentGroupCollection,
} from '@ve-pro/types/track.type'
import { defineSegmentProperties, resolveSegment } from '../resolve-segment'
import {
  getConflictSegment,
  insertToAudioTrack,
  insertToOtherTrack,
} from '../add-segment-action'
import { addSegment, isFragmentTrack, remove } from '../tracks-actions'
import { SegmentType, SourceSegmentIdBizType } from '@ve-pro/enums/segment'
import { isSegmentGroup, isSegmentGroupChild } from './segment-group'
import { AssetType } from '@ve-pro/enums/asset'
import { containLayout } from '@ve-pro/store/tracks/segment/segment-ratio'
import { durationToFrames } from '@ve-pro/store/tracks/frame'
import { setDefaultAsset } from '@ve-pro/ve/aside/modular/asset-group-shared'
import { language } from '@/common/storage'
import { t } from '@ve-pro/ve/lang'
import { ProjectConfig } from '@ve-pro/api/project/type'
import { setActiveSegment } from '../toggle-active-segment'
import { updateSegmentFadeDuration } from './segment-fade'
import {
  getTransition,
  updateVideoSegmentTransition,
} from './segment-transition'
import { updateSegmentAnimationDuration } from './segment-animation'
import { updateMainTrack } from '../update-main-track'

/**
 * 判断是否是元件
 * 根据是否包含track_id字段
 * @param segment
 * @returns boolean
 */
export function isSegment(segment: any): segment is SegmentCollection {
  return typeof segment === 'object'
    ? segment.hasOwnProperty('track_id')
    : false
}

/**
 * 获取所有的元件，包括普通元件、组和子元件
 */
export function getAllSegments(): SegmentCollection[] {
  const trackStore = useTrackStore()

  return _getAllSegments(trackStore.segments, trackStore.segmentGroups)
}

export function _getAllSegments(
  segments: SegmentCollection[],
  segmentGroups: SegmentGroups,
): SegmentCollection[] {
  return segments.reduce((prev, curr) => {
    const groups = segmentGroups[curr.id]
    if (groups) {
      return [...prev, curr, ...groups]
    }

    return [...prev, curr]
  }, [] as SegmentCollection[])
}

/**
 * 替换素材后生成新元件的方法
 */
export function replaceSegmentMaterial<T extends SegmentReplaceableCollection>(
  target: T,
  asset: SegmentMaterial,
): T {
  const trackStore = useTrackStore()

  const baseSegment = resolveSegment({
    ...asset,
    asset_id: asset.id,
  }) as T

  const segment = {
    ...baseSegment,
    ...target,
    material: baseSegment.material,
    asset_id: baseSegment.asset_id,
    asset_type: baseSegment.asset_type,
    name: baseSegment.name,
  }

  if (
    baseSegment.asset_type === AssetType.Video ||
    baseSegment.asset_type === AssetType.Audio
  ) {
    segment.duration = baseSegment.duration
    segment.duration_frames = durationToFrames(baseSegment.duration)
    defineSegmentProperties(segment, {
      material_duration: baseSegment.material?.duration ?? baseSegment.duration,
      trim_start_time: 0,
      duration: baseSegment.duration,
    })
  } else {
    // 图片继承原元件时长
    segment.duration = target.duration
    segment.duration_frames = durationToFrames(target.duration)
    defineSegmentProperties(segment, {
      material_duration: target.material?.duration ?? target.duration,
      trim_start_time: 0,
      duration: target.duration,
    })
  }

  if (
    segment.type === SegmentType.Video &&
    target.type === SegmentType.Video &&
    segment.ratios
  ) {
    const _target =
      trackStore.segments.find((s) => s.id === target.pid) ?? target

    Object.keys(segment.ratios).forEach((ratioType) => {
      const ratio = segment.ratios[ratioType as RatioTypeUnion]
      const targetRatio = _target.ratios[ratioType as RatioTypeUnion]

      if (ratio && targetRatio) {
        const layout = containLayout(
          { width: asset.width, height: asset.height },
          { width: targetRatio.width, height: targetRatio.height },
        )

        ratio.width = layout.width
        ratio.height = layout.height
      }
    })
  }

  return segment
}

export function replaceSegment(
  newSegment: SegmentCollection,
  segment: SegmentCollection,
): void {
  const trackStore = useTrackStore()

  replaceTrackSegment(newSegment, segment)

  // 如果是子元件
  if (trackStore.activeSegments.includes(segment.id)) {
    setActiveSegment(newSegment, trackStore.activeSegments)
  }

  const track = trackStore.tracks.find((t) => t.id === newSegment.track_id)
  if (track && trackStore.isMainTrack(track)) {
    // 淡入淡出
    updateSegmentFadeDuration(newSegment)
    // fadePercent
    // 动画
    updateSegmentAnimationDuration(newSegment)
    if (newSegment.type === SegmentType.Video) {
      const { before, behind } = getTransition(newSegment)

      if (before) {
        before.behind = newSegment
      }

      if (behind) {
        behind.before = newSegment
      }

      updateVideoSegmentTransition(newSegment)
    }

    updateMainTrack()
  }
}

// 把元件加入轨道中
export function addSegmentToTrack(segment: SegmentCollection): void {
  const trackStore = useTrackStore()
  const track = trackStore.tracks.find((t) => t.id === segment.track_id)

  if (isSegmentGroup(segment)) return

  if (segment.type === SegmentType.Audio) {
    insertToAudioTrack(segment)
  } else if (track) {
    const mainTrack = isFragmentTrack(track)
      ? trackStore.getMainTrack(
          trackStore.getTracksByFragmentSegmentId(track.fragment_segment_id!),
        )
      : trackStore.defaultExcludeFragmentMainTrack
    const isMainTrack = mainTrack?.id === track.id
    // addSegment(segment, track)
    if (!isMainTrack) {
      // 当前元件在轨道不冲突
      if (!getConflictSegment(segment, track)) {
        addSegment(segment, track)
      } else {
        insertToOtherTrack(segment)
        // insertToAudioTrack(segment)
      }
    } else {
      addSegment(segment, track)
    }
  }
}

/**
 * 替换元件素质
 * @param segment 新的元件
 * @param target 要被替换的元件
 */
export function replaceTrackSegment(
  segment: SegmentCollection,
  target: SegmentCollection,
): void {
  // removeSegment(target, false)
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()
  // 子元件
  const childArray = trackStore.segmentGroups[target.pid]
  console.log('子元件', childArray, segment, target)
  if (childArray && childArray.some((t) => t.id === target.id)) {
    remove(target, childArray)
    childArray.push(segment)

    const group = trackStore.segments.find(
      (t): t is SegmentGroupCollection => t.id === target.pid,
    )
    const assetGroup = projectStore.assetGroups.find(
      (t) => t.id === group?.asset_group_id,
    )

    if (group && assetGroup) {
      // 如果替换的子元件是资产组的默认资源，则更新默认资源
      if (assetGroup.default_asset_id === target.asset_id) {
        setDefaultAsset(segment)
      }
    }
  } else {
    const track = trackStore.tracks.find((t) => t.id === target.track_id)
    if (track) {
      remove(target, track.segments)
    }

    addSegmentToTrack(segment)
  }
}

/**
 * 元件是否支持静音
 */
export function isSupportMute(
  segment: SegmentCollection,
): segment is SegmentMuteCollection {
  return (
    segment.asset_type === AssetType.Video ||
    segment.asset_type === AssetType.Audio
  )
}

/**
 * 判断元件归属于主轨道
 */
export function inMainTrack(segment: SegmentCollection): boolean {
  const trackStore = useTrackStore()

  // return (
  //   trackStore.tracks.find((track) => track.id === segment.track_id)?.is_main ??
  //   false
  return segment.track_id === trackStore.mainTack?.id

  // return (
  //   trackStore.tracks.find((track) => track.id === segment.track_id)?.is_main ??
  //   false
  // )
  // return (
  //   segment.track_id ===
  //   trackStore.tracks.find((t) =>
  //     t.segments.some((s) => s.type === SegmentType.Video && s.is_main_video),
  //   )?.id
  // )
}

export function getSegmentPitNaming(
  segment: SegmentCollection,
  options?: {
    ptLang?: string
    showMaterialName?: boolean
  },
): string {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  return pureGetSegmentPitNaming(
    segment,
    projectStore.configs.aiRemove,
    trackStore.segmentGroups,
    options,
  )
}

export function pureGetSegmentPitNaming(
  segment: SegmentCollection,
  aiRemove: ProjectConfig['aiRemove'],
  segmentGroups: SegmentGroups,
  options?: {
    ptLang?: string
    showMaterialName?: boolean
  },
): string {
  const { showMaterialName = true } = options ?? {}

  if (segment.biz_type === SourceSegmentIdBizType.Elimination) {
    return getEliminationPitNaming(
      segment,
      aiRemove,
      segmentGroups,
      showMaterialName,
    )
  }

  if (segment.type === SegmentType.Video && segment.is_main_video) {
    return t('replaceMaterial.mainVideo')
  }

  return getPitNaming(segment.pit_location, options?.ptLang) || segment.name
}

export function getEliminationPitNaming(
  segment: SegmentCollection,
  aiRemove: ProjectConfig['aiRemove'],
  segmentGroups: SegmentGroups,
  showMaterialName = true,
): string {
  const key = Number(
    Object.keys(aiRemove).find((k) => {
      return aiRemove[k as any]?.items.some(
        (item) => item.segmentId === segment.id,
      )
    }) || 0,
  ) as unknown as number

  const target = aiRemove[key as any]

  const order =
    (target &&
      target.items.findIndex((item) => item.segmentId === segment.id) + 1) ||
    0

  const children = Object.values(segmentGroups).find((children) =>
    children.some((child) => child.id === key),
  )
  const child = children?.find((child) => child.id === key)

  const materialName = showMaterialName
    ? `（${child?.material?.name || '-'}）`
    : ''

  return `${order}_${t('aiRemove.type.replace')}${materialName}`
}

export function getPitNaming(
  pit_location: SegmentCollection['pit_location'],
  ptLang?: string,
): string | undefined {
  const maps = {
    'zh-cn': pit_location?.name,
    en: pit_location?.name_en || pit_location?.name,
    ja: pit_location?.name_en || pit_location?.name,
  }
  return maps[(ptLang ?? language.get()) as keyof typeof maps]
}

export function isDefaultBizTypeSegment(segment: SegmentCollection): boolean {
  return !segment.biz_type
}
